import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  Camera,
  Check,
  ContactRound,
  Heart,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Save,
  ScanFace,
  Tag,
  UserRound,
} from "lucide-react";

const initialForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  type: "0",
  age: "",
  sex: "",
  customer_id: "",
  store_id: "",
  preferences: "",
  note: "",
  tag: "",
};

function normalizeImage(url) {
  return url?.replace(/^http:\/\//i, "https://") || "";
}

export default function MemberAdd() {
  const navigate = useNavigate();
  const { ulucu_id } = useParams();
  const location = useLocation();
  const [profileData, setProfileData] = useState(null);
  const [form, setForm] = useState({
    ...initialForm,
    age: location.state?.age || "",
    sex: location.state?.sex || "",
    store_id: String(location.state?.store_id || ""),
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/visit-log/${ulucu_id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
        const json = await response.json();
        if (!response.ok) throw new Error("ไม่พบข้อมูลผู้มาเยือน");
        setProfileData(json.data);
        const profile = json.data.profile || {};
        setForm((current) => ({
          ...current,
          age: current.age || profile.age || "",
          sex: current.sex || profile.sex || "",
          store_id: current.store_id || String(profile.store_id || ""),
        }));
      } catch (loadError) {
        setError(loadError.message);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [ulucu_id]);

  const completeness = useMemo(() => {
    const required = [form.firstName, form.lastName, form.phone, form.sex, form.age, form.store_id];
    return Math.round((required.filter((value) => String(value || "").trim()).length / required.length) * 100);
  }, [form]);

  const visitedStores = useMemo(() => {
    const visits = Object.values(profileData?.byDate || {})
      .flat()
      .sort((a, b) => String(b.arrive_time_thai || "").localeCompare(String(a.arrive_time_thai || "")));
    const storeMap = new Map();

    visits.forEach((visit) => {
      const storeId = String(visit.store_id || "");
      if (!storeId || storeMap.has(storeId)) return;
      storeMap.set(storeId, {
        store_id: storeId,
        device_name: visit.device_name || `Store ${storeId}`,
        last_visit: visit.arrive_time_thai || "",
      });
    });

    return [...storeMap.values()];
  }, [profileData]);

  const handleChange = (event) => setForm({ ...form, [event.target.name]: event.target.value });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.firstName.trim() || !form.lastName.trim()) return setError("กรุณากรอกชื่อและนามสกุล");
    if (!form.phone.trim()) return setError("กรุณากรอกเบอร์โทรศัพท์");
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return setError("รูปแบบอีเมลไม่ถูกต้อง");
    if (!form.sex || !form.age) return setError("กรุณาระบุเพศและอายุ");
    if (!form.store_id) return setError("ไม่พบสาขาจากการตรวจจับล่าสุด");

    const payload = {
      ulucu_id,
      realname: `${form.firstName.trim()} ${form.lastName.trim()}`,
      mobile: form.phone.trim(),
      email: form.email.trim(),
      isvip: form.type,
      age: form.age,
      sex: form.sex,
      store_id: form.store_id,
      customer_id: form.customer_id.trim(),
      preferences: form.preferences.trim(),
      note: form.note.trim(),
      tag: form.tag.trim(),
      source: "ULUCU",
    };

    setSaving(true);
    try {
      const response = await fetch("/api/members", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(payload),
      });
      const json = await response.json();
      if (!response.ok) {
        const messages = {
          "Member already exists": "ผู้มาเยือนรายนี้เป็นสมาชิกอยู่แล้ว",
          "visitor not found": "ไม่พบผู้มาเยือนในสาขานี้",
          "store not allowed": "ไม่มีสิทธิ์เพิ่มสมาชิกในสาขานี้",
        };
        throw new Error(messages[json.message] || json.message || "ไม่สามารถบันทึกสมาชิกได้");
      }
      navigate(`/member/${ulucu_id}`);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 p-5 md:p-8"><div className="mx-auto max-w-6xl animate-pulse space-y-6"><div className="h-12 w-28 rounded-xl bg-slate-200" /><div className="h-48 rounded-3xl bg-white" /><div className="h-[600px] rounded-2xl bg-white" /></div></div>;

  if (profileData?.is_member) return <div className="min-h-screen bg-slate-50 p-6"><div className="mx-auto max-w-lg rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm"><BadgeCheck size={42} className="mx-auto text-emerald-500" /><h1 className="mt-4 text-xl font-semibold text-slate-900">เป็นสมาชิกอยู่แล้ว</h1><p className="mt-2 text-sm text-slate-500">{profileData.member?.realname || `รหัส #${ulucu_id}`}</p><button onClick={() => navigate(`/member/${ulucu_id}`)} className="mt-6 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white">ดูข้อมูลสมาชิก</button></div></div>;

  const profile = profileData?.profile || {};
  const sourceImage = normalizeImage(location.state?.image || profile.arrive_image_url);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between"><button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-teal-700"><ArrowLeft size={17} />กลับไปหน้าโปรไฟล์</button><div className="hidden items-center gap-2 text-xs text-slate-400 sm:flex"><ScanFace size={14} />Source: ULUCU · #{String(ulucu_id).slice(-8)}</div></div>

        <section className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm"><div className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-teal-50 via-white to-white" /><div className="relative flex flex-col gap-5 p-6 sm:flex-row sm:items-center"><img src={sourceImage} alt="รูปจากการตรวจจับล่าสุด" className="h-28 w-28 shrink-0 rounded-2xl border-4 border-white object-cover shadow-lg ring-1 ring-slate-200" /><div className="min-w-0 flex-1"><div className="flex items-center gap-2 text-sm font-medium text-teal-700"><Camera size={16} />ข้อมูลจากการตรวจจับล่าสุด</div><h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">เพิ่มผู้มาเยือนเป็นสมาชิก</h1><p className="mt-1 text-sm text-slate-500">ตรวจสอบข้อมูลที่ระบบตรวจพบ แล้วเติมข้อมูลสำหรับ CRM ให้ครบถ้วน</p><div className="mt-3 flex flex-wrap gap-2 text-xs"><span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">{profile.sex === "1" ? "ผู้ชาย" : profile.sex === "0" ? "ผู้หญิง" : "ไม่ระบุเพศ"}</span><span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">อายุประมาณ {profile.age || "—"} ปี</span><span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">Store {profile.store_id || "—"}</span></div></div><div className="w-full rounded-2xl border border-slate-200 bg-white/80 p-4 sm:w-48"><div className="flex items-center justify-between text-xs"><span className="text-slate-500">ความครบถ้วน</span><strong className="text-teal-700">{completeness}%</strong></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-all" style={{ width: `${completeness}%` }} /></div><p className="mt-2 text-[11px] text-slate-400">กรอกข้อมูลหลักให้ครบก่อนบันทึก</p></div></div></section>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FormSection icon={ContactRound} title="ข้อมูลสมาชิก" description="ชื่อและข้อมูลติดต่อสำหรับใช้งานในระบบ CRM">
            <div className="grid gap-5 md:grid-cols-2"><Field label="ชื่อ" required><input name="firstName" value={form.firstName} onChange={handleChange} placeholder="ชื่อจริง" className="input-control" /></Field><Field label="นามสกุล" required><input name="lastName" value={form.lastName} onChange={handleChange} placeholder="นามสกุล" className="input-control" /></Field><Field label="เบอร์โทรศัพท์" required><div className="relative"><Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="08x-xxx-xxxx" className="input-control pl-9" /></div></Field><Field label="อีเมล"><div className="relative"><Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input name="email" type="email" value={form.email} onChange={handleChange} placeholder="name@example.com" className="input-control pl-9" /></div></Field><Field label="Customer ID"><input name="customer_id" value={form.customer_id} onChange={handleChange} placeholder="รหัสลูกค้าภายในองค์กร" className="input-control font-mono" /></Field><Field label="ประเภทสมาชิก" required><select name="type" value={form.type} onChange={handleChange} className="input-control"><option value="0">ลูกค้าสมาชิกทั่วไป</option><option value="1">ลูกค้า VIP</option><option value="2">พนักงาน</option></select></Field></div>
          </FormSection>

          <FormSection icon={UserRound} title="ข้อมูลประชากรและสาขา" description="ข้อมูลบางส่วนถูกเติมจากระบบตรวจจับ สามารถตรวจสอบและแก้ไขได้">
            <div className="grid gap-5 md:grid-cols-3"><Field label="เพศ" required><select name="sex" value={form.sex} onChange={handleChange} className="input-control"><option value="">เลือกเพศ</option><option value="1">ผู้ชาย</option><option value="0">ผู้หญิง</option></select></Field><Field label="อายุ" required><input name="age" type="number" min="1" max="120" value={form.age} onChange={handleChange} placeholder="อายุ" className="input-control" /></Field><Field label="สาขาสำหรับสมาชิก" required><div className="relative"><MapPin size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><select name="store_id" value={form.store_id} onChange={handleChange} className="input-control pl-9"><option value="">เลือกจากสาขาที่เคยตรวจพบ</option>{visitedStores.map((store) => <option key={store.store_id} value={store.store_id}>{store.device_name} · Store {store.store_id}</option>)}</select></div><p className="mt-1.5 text-[11px] text-slate-400">แสดงเฉพาะสาขาที่บุคคลนี้เคยเดินผ่าน ({visitedStores.length} สาขา)</p></Field></div>
          </FormSection>

          <FormSection icon={Heart} title="ข้อมูลสำหรับดูแลลูกค้า" description="บันทึกความสนใจและบริบทที่ช่วยให้ทีมให้บริการได้ดีขึ้น">
            <div className="grid gap-5 md:grid-cols-2"><Field label="ความชอบ"><textarea name="preferences" value={form.preferences} onChange={handleChange} rows={4} placeholder="เช่น ชอบสินค้ากลุ่ม A, สีดำ, สนใจโปรโมชั่น..." className="w-full resize-none rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10" /></Field><Field label="หมายเหตุ"><textarea name="note" value={form.note} onChange={handleChange} rows={4} placeholder="ข้อมูลเพิ่มเติมหรือเรื่องที่ต้องติดตาม..." className="w-full resize-none rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10" /></Field><Field label="Tag"><div className="relative"><Tag size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input name="tag" value={form.tag} onChange={handleChange} placeholder="เช่น ลูกค้าประจำ, สนใจสินค้า A" className="input-control pl-9" /></div></Field></div>
          </FormSection>

          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <div className="sticky bottom-4 flex flex-col-reverse gap-2 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur sm:flex-row sm:items-center sm:justify-between"><p className="flex items-center gap-2 text-xs text-slate-400"><Check size={14} className="text-teal-500" />ข้อมูลจะถูกเชื่อมกับประวัติการเข้าใช้บริการเดิม</p><div className="flex gap-2"><button type="button" onClick={() => navigate(-1)} disabled={saving} className="h-11 rounded-xl px-4 text-sm font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button><button type="submit" disabled={saving} className="flex h-11 items-center gap-2 rounded-xl bg-teal-600 px-6 text-sm font-medium text-white transition hover:bg-teal-700 disabled:opacity-50">{saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}{saving ? "กำลังบันทึก" : "บันทึกสมาชิก"}</button></div></div>
        </form>
      </div>
    </div>
  );
}

function FormSection({ icon: Icon, title, description, children }) { return <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:p-6"><div className="mb-5 flex items-start gap-3"><div className="rounded-xl bg-teal-50 p-2.5 text-teal-700 ring-1 ring-teal-100"><Icon size={19} /></div><div><h2 className="font-semibold text-slate-900">{title}</h2><p className="mt-0.5 text-xs text-slate-400">{description}</p></div></div>{children}</section>; }
function Field({ label, required, children }) { return <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-600">{label}{required && <span className="ml-1 text-red-500">*</span>}</span>{children}</label>; }
